import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowRight, Package, Code, Bot, Calculator, Microscope, CheckCircle2,
  Truck, Gift, Shield, Star, Sparkles
} from 'lucide-react';

const kitCategories = [
  {
    division: 'Ready Set STEM',
    color: 'bg-rsc-blue',
    textColor: 'text-rsc-blue',
    icon: Code,
    kits: [
      { name: 'Scratch Starter Kit', ages: 'Ages 5-9', contents: ['Activity cards with 50+ coding challenges', 'Coding journal & project planner', 'Pixel art grid sheets', 'Sticker rewards pack (100+ stickers)', 'QR codes linking to bonus Scratch projects'], included: 'Scratch Jr. Adventures, Scratch Game Design' },
      { name: 'Python Developer Kit', ages: 'Ages 10-14', contents: ['Python coding notebook with exercises', 'Python & JavaScript reference cards', 'Debugging checklist & flowchart templates', 'USB drive with starter projects & assets', 'Code review worksheet pack'], included: 'Python Fundamentals, Web Development' },
      { name: 'Robotics Builder Kit', ages: 'Ages 8-18', contents: ['LEGO SPIKE Essential components', 'Sensor pack (color, distance, force)', 'Motor & gear set', 'Engineering notebook', 'Competition field setup guide'], included: 'Robotics with LEGO SPIKE, Robotics Teams' },
    ]
  },
  {
    division: 'Ready Set Math',
    color: 'bg-indigo-600',
    textColor: 'text-indigo-600',
    icon: Calculator,
    kits: [
      { name: 'Math Explorer Kit', ages: 'Ages 5-10', contents: ['Base-10 blocks (ones, tens, hundreds, thousands)', 'Fraction tiles & circle set', 'Geometric solids (12 shapes)', 'Math puzzle cards (100+ problems)', 'Dry-erase number line & marker', 'Math journal with problem templates'], included: 'Math Foundations & Enrichment' },
      { name: 'Competition Math Toolkit', ages: 'Ages 10-18', contents: ['AMC/MATHCOUNTS practice booklet (200+ problems)', 'Competition countdown timer', 'Problem-solving strategy cards (20 strategies)', 'Formula reference sheet (laminated)', 'Scratch paper pad', 'Competition pencil set'], included: 'AMC 8, AMC 10/12, MATHCOUNTS, Math Olympiad' },
      { name: 'Math Arena Battle Pack', ages: 'Ages 8-14', contents: ['200+ head-to-head math battle cards', 'Personal mini whiteboard & markers', 'Speed challenge timer', 'Achievement sticker collection', 'Tournament bracket sheets', 'Score tracking cards'], included: 'All Math courses (used in Math Arena sessions)' },
      { name: 'Probability & Statistics Kit', ages: 'Ages 10-14', contents: ['Complete dice set (4, 6, 8, 10, 12, 20-sided)', 'Probability spinners (4 types)', 'Playing card deck', 'Graphing paper pad', 'Coin sets for experiments', 'Data collection worksheets'], included: 'AMC 8 Prep, Math Foundations' },
    ]
  },
  {
    division: 'Ready Set Science',
    color: 'bg-emerald-600',
    textColor: 'text-emerald-600',
    icon: Microscope,
    kits: [
      { name: 'Young Scientist Lab Kit', ages: 'Ages 5-10', contents: ['6 plastic test tubes with rack', 'Magnifying glass (5x)', 'pH test strips (50 strips)', 'Petri dishes (10 pack)', 'Seed pods for growing experiments', 'Science journal with experiment templates', 'Kid-size safety goggles'], included: 'Young Scientists Explorer' },
      { name: 'Science Olympiad Prep Kit', ages: 'Ages 11-18', contents: ['Professional lab notebook', 'Basic dissection tool set', 'Mineral & rock sample collection (15 specimens)', 'Forensic fingerprint powder & brush kit', 'UV flashlight for forensic analysis', 'Balsa wood & glue for bridge building', 'Event reference guide'], included: 'Science Olympiad Div B & C' },
      { name: 'Chemistry Lab-in-a-Box', ages: 'Ages 12-18', contents: ['2 glass beakers (100ml, 250ml)', 'Graduated cylinder (50ml)', 'Litmus paper pack (200 strips)', 'Baking soda & citric acid supplies', 'Food coloring set', 'Kid-size lab coat', 'Safety goggles', 'Lab manual with 20 experiments'], included: 'Chemistry & Physics Lab, Science Olympiad' },
      { name: 'Anatomy & Physiology Kit', ages: 'Ages 12-18', contents: ['3D mini heart model (detachable)', '3D brain model (cross-section)', '3D eye & ear models', 'Skeleton jigsaw puzzle', 'Anatomy coloring book', 'Organ system flash cards (100 cards)', 'Body systems poster'], included: 'Biomedical Sciences, Science Olympiad' },
      { name: 'Environmental Science Field Kit', ages: 'Ages 8-14', contents: ['Water quality test strips (50)', 'Soil pH testing kit', 'Digital thermometer', 'Sample collection jars (6)', 'Field notebook (waterproof)', 'Magnifying glass', 'Plant & insect identification cards'], included: 'Environmental & Earth Science' },
    ]
  },
];

export default function STEMKitStorePage() {
  return (
    <div data-testid="stem-kit-store">
      {/* Hero */}
      <section className="relative py-20 md:py-28 gradient-hero overflow-hidden">
        <div className="container-app relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 text-rsc-yellow text-sm font-bold mb-6">
            <Package className="w-4 h-4" /> Included With Every Course — No Extra Cost
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">
            STEM Kits
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-4">
            Every Ready Set STEM course includes a hands-on STEM kit shipped directly to your door.
            Real equipment. Real experiments. Real learning.
          </p>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            No hidden fees. No extra charges. Kits are included in your course tuition — 
            and completely free for families in our APS Training Academy programs.
          </p>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-8 bg-white border-b">
        <div className="container-app">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Gift, label: 'Included Free', desc: 'With every course enrollment' },
              { icon: Truck, label: 'Shipped to You', desc: 'Delivered before first class' },
              { icon: Shield, label: 'Safe Materials', desc: 'Age-appropriate & non-toxic' },
              { icon: Sparkles, label: 'Keep Forever', desc: 'Kits are yours to keep' },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="flex flex-col items-center gap-2">
                  <Icon className="w-6 h-6 text-rsc-blue" />
                  <p className="font-bold text-sm text-rsc-navy">{v.label}</p>
                  <p className="text-xs text-gray-500">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Kit Categories */}
      {kitCategories.map((cat, ci) => {
        const CatIcon = cat.icon;
        return (
          <section key={ci} className={`section-padding ${ci % 2 === 1 ? 'bg-gray-50' : ''}`}>
            <div className="container-app">
              <div className="flex items-center gap-4 mb-10">
                <div className={`w-14 h-14 rounded-2xl ${cat.color} flex items-center justify-center`}>
                  <CatIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-rsc-navy">{cat.division} Kits</h2>
                  <p className="text-gray-500 text-sm">Included with {cat.division} courses</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cat.kits.map((kit, ki) => (
                  <Card key={ki} className="card-marketing h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-heading text-lg font-bold text-rsc-navy">{kit.name}</h3>
                        <Badge className={`${cat.color} text-white text-xs`}>{kit.ages}</Badge>
                      </div>
                      <div className="space-y-1.5 mb-4">
                        {kit.contents.map((item, ii) => (
                          <div key={ii} className="flex items-start gap-2">
                            <CheckCircle2 className={`w-4 h-4 ${cat.textColor} flex-shrink-0 mt-0.5`} />
                            <span className="text-sm text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                      <div className="pt-3 border-t">
                        <p className="text-xs text-gray-500"><span className="font-semibold">Included with:</span> {kit.included}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-rsc-blue to-rsc-navy text-white">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Every Kit. Every Course. Included.</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">No hidden costs. No extra charges. Just hands-on STEM learning delivered to your door.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/catalog"><Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">Browse Courses <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/contact"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Book Free Trial</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
